import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const NaturalResource: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/NaturalResource.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/NaturalResource.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Sun as a Natural Resource – Class 3 Science | Importance & Solar Energy | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn how the Sun is a natural resource in Class 3 Science. Discover its importance for plants, animals, and humans, and understand solar energy with simple facts at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>☀️ Chapter 11: Sun as a Natural Resource</h1>

                            <h2>🌞 What is the Sun?</h2>
                            <p>The <strong>Sun</strong> is a huge ball of hot gases. It is the <strong>closest star to Earth</strong> and the <strong>main source of heat and light</strong>. Life would not be possible without the Sun.</p>

                            <div className="highlight">
                                <strong>Did you know?</strong> Without the Sun, Earth would be cold and dark!
                            </div>

                            <h2>🔥 Importance of the Sun</h2>
                            <ul>
                                <li>Gives us light during the day</li>
                                <li>Provides warmth</li>
                                <li>Helps plants make food (photosynthesis)</li>
                                <li>Dries clothes and wet surfaces</li>
                                <li>Helps form clouds and rain</li>
                            </ul>

                            <h2>🌱 Sun and Plants</h2>
                            <p>Plants need sunlight to make their own food. This process is called <strong>photosynthesis</strong>. Without sunlight, plants cannot grow properly.</p>

                            <h2>🐾 Sun and Animals</h2>
                            <p>Animals depend on <strong>plants for food</strong>, and since plants need sunlight, animals also depend on the Sun. Sunlight helps keep animals warm.</p>

                            <h2>🧍‍♂️ Sun and Humans</h2>
                            <ul>
                                <li>We need sunlight to make <strong>vitamin D</strong></li>
                                <li>Keeps us warm</li>
                                <li>Used in <strong>solar panels</strong> to produce energy</li>
                            </ul>

                            <h2>☀️ Solar Energy</h2>
                            <p><strong>Solar energy</strong> is the energy we get from the Sun. It is clean, renewable, and non-polluting. It is used to:</p>
                            <ul>
                                <li>Light homes and streets</li>
                                <li>Heat water</li>
                                <li>Cook food in solar cookers</li>
                                <li>Run solar gadgets</li>
                            </ul>

                            <h2>🛡️ Safety from the Sun</h2>
                            <ul>
                                <li>Wear hats or caps</li>
                                <li>Use umbrellas</li>
                                <li>Wear sunglasses</li>
                                <li>Apply sunscreen</li>
                            </ul>

                            <h2>✅ Quick Recap</h2>
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Topic</th>
                                        <th>Key Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>What is the Sun?</td>
                                        <td>Closest star, gives light and heat</td>
                                    </tr>
                                    <tr>
                                        <td>Importance</td>
                                        <td>Light, warmth, plant growth, rain</td>
                                    </tr>
                                    <tr>
                                        <td>Plants & Sun</td>
                                        <td>Photosynthesis needs sunlight</td>
                                    </tr>
                                    <tr>
                                        <td>Animals & Sun</td>
                                        <td>Depend on plants that need the Sun</td>
                                    </tr>
                                    <tr>
                                        <td>Humans & Sun</td>
                                        <td>Health, drying, energy source</td>
                                    </tr>
                                    <tr>
                                        <td>Solar Energy</td>
                                        <td>Used in panels, cookers, heaters</td>
                                    </tr>
                                    <tr>
                                        <td>Sun Safety</td>
                                        <td>Hats, sunscreen, sunglasses</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>☀️ Class 3 Science – Chapter 11: Sun as a Natural Resource</h1>

                        <h2>🌞 What is the Sun?</h2>
                        <p>The <strong>Sun</strong> is a huge ball of hot gases. It is the <strong>closest star to Earth</strong> and the <strong>main source of heat and light</strong>. Life would not be possible without the Sun.</p>

                        <div className="highlight">
                            <strong>Did you know?</strong> Without the Sun, Earth would be cold and dark!
                        </div>

                        <h2>🔥 Importance of the Sun</h2>
                        <ul>
                            <li>Gives us light during the day</li>
                            <li>Provides warmth</li>
                            <li>Helps plants make food (photosynthesis)</li>
                            <li>Dries clothes and wet surfaces</li>
                            <li>Helps form clouds and rain</li>
                        </ul>

                        <h2>🌱 Sun and Plants</h2>
                        <p>Plants need sunlight to make their own food. This process is called <strong>photosynthesis</strong>. Without sunlight, plants cannot grow properly.</p>

                        <h2>🐾 Sun and Animals</h2>
                        <p>Animals depend on <strong>plants for food</strong>, and since plants need sunlight, animals also depend on the Sun. Sunlight helps keep animals warm.</p>

                        <h2>🧍‍♂️ Sun and Humans</h2>
                        <ul>
                            <li>We need sunlight to make <strong>vitamin D</strong></li>
                            <li>Keeps us warm</li>
                            <li>Used in <strong>solar panels</strong> to produce energy</li>
                        </ul>

                        <h2>☀️ Solar Energy</h2>
                        <p><strong>Solar energy</strong> is the energy we get from the Sun. It is clean, renewable, and non-polluting. It is used to:</p>
                        <ul>
                            <li>Light homes and streets</li>
                            <li>Heat water</li>
                            <li>Cook food in solar cookers</li>
                            <li>Run solar gadgets</li>
                        </ul>

                        <h2>🛡️ Safety from the Sun</h2>
                        <ul>
                            <li>Wear hats or caps</li>
                            <li>Use umbrellas</li>
                            <li>Wear sunglasses</li>
                            <li>Apply sunscreen</li>
                        </ul>

                        <h2>✅ Quick Recap</h2>
                        <table>
                            <tr>
                                <th>Topic</th>
                                <th>Key Points</th>
                            </tr>
                            <tr>
                                <td>What is the Sun?</td>
                                <td>Closest star, gives light and heat</td>
                            </tr>
                            <tr>
                                <td>Importance</td>
                                <td>Light, warmth, plant growth, rain</td>
                            </tr>
                            <tr>
                                <td>Plants & Sun</td>
                                <td>Photosynthesis needs sunlight</td>
                            </tr>
                            <tr>
                                <td>Animals & Sun</td>
                                <td>Depend on plants that need the Sun</td>
                            </tr>
                            <tr>
                                <td>Humans & Sun</td>
                                <td>Health, drying, energy source</td>
                            </tr>
                            <tr>
                                <td>Solar Energy</td>
                                <td>Used in panels, cookers, heaters</td>
                            </tr>
                            <tr>
                                <td>Sun Safety</td>
                                <td>Hats, sunscreen, sunglasses</td>
                            </tr>
                        </table>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default NaturalResource;
